// Combat resolution. Pure. No React, no Three.js. Owns the
// damage-exchange math, attackable-cell enumeration, and valid-target
// enumeration for the Move and Attack basic ability and its
// attack-only variant.

import { hasActiveStealth } from './stealth';
import { hasKeyword } from './keywordQuery';
import { getRangedValue, isProjectileAttack } from './ranged';
import { countsAsUnit } from './typeline';
//
// ── Codex anchors ──
//
//   Attack / Strike. A unit attacks a target. By default the
//     attacker and the defender each take damage equal to the
//     other's power, simultaneously. Wards intercept damage before
//     it reduces life (consumed per intercept). Lethal kills the
//     target regardless of remaining life (Phase 3c. Not yet
//     enforced here).
//   Move and Attack. "Tap → This unit may take one step. Then,
//     it may attack." Movement and attack are sub-events of the
//     same activated ability; movement happens first, then attack
//     target declared at the destination.
//   Attack location. Codex Attack: "You may only attack enemy units
//     or the surface of enemy sites that are at your location." Melee
//     hits the unit's OWN location only; adjacency is reached by the
//     Move-and-Attack movement step (move onto the enemy's square,
//     then strike). Ranged shoots a projectile to another location.
//   Surface vs subsurface. The default attack hits surface cards
//     only. Subsurface units (Burrowed / Submerged) are NOT
//     attackable unless the attacker has a specific subsurface-strike
//     ability (Phase 3b).
//   Airborne. Codex: "[An Airborne unit] cannot be targeted by
//     attacks, unless the attacking unit also has Airborne." So a
//     non-Airborne attacker cannot pick an Airborne defender, even
//     if both are on the surface. Sites are not Airborne and not
//     subject to this gate.
//   Stealth. Codex: "Minions with Stealth cannot be targeted by
//     spells or abilities from your opponents (including attacks)."
//     A Stealth defender drops out of valid attack targets entirely.
//     Sites can't have Stealth so the gate doesn't apply to them.
//     `hasActiveStealth` already accounts for the disabled exception.
//   Site as target. A site itself is a valid attack target; killing
//     a site replaces it with Rubble (codex Rubble entry).
//
// ── Phase 3a scope ──
//
//   Attacks resolve at the unit's own location (move onto, then strike).
//   Surface targets only.
//   Damage exchange + Ward interception. Lethal deferred.
//   Site-as-target enumeration but resolution is "deal damage and
//     route to Cemetery if killed." Rubble replacement is a Phase
//     3c follow-up.
//   No combat-triggered automation yet (on_attack, on_damage_dealt,
//     on_damage_taken. Schema gap).

// Ownerless site: a Rubble "is controlled by no one" (codex Rubble). Its
// board orientation (rotated) is inherited from the destroyed site, so a
// naive orientation-only seat derivation would route life loss to a player.
const OWNERLESS_SITE_IDS = new Set(['sorcery-rubble']);

/**
 * Pure attack-target descriptor: derives { kind, seat, ownerless } from the
 * target card. This is the single engine home for the ownerless-site rule.
 *
 * Codex Damage: a site causes life loss to "the player who controls the
 * site." A site's controller is read from board orientation. A rotated
 * card belongs to p2, otherwise p1 (`seatFromOrientation`). But an ownerless
 * site (Rubble) has NO controller, so it carries no life-loss seat: the
 * attack stays legal (on_attack/on_strike triggers still fire), and
 * resolveFight suppresses the life loss when seat is null.
 *
 * @param card     target cardInstance ({ id, cardId, isSite, type, rotated })
 * @param seatFromOrientation (card) => 'p1'|'p2'. Host's orientation→seat fn
 * @returns {{ kind:'unit'|'site'|'avatar', instanceId, seat, ownerless }}
 */
export function buildAttackTargetDescriptor(card, seatFromOrientation) {
  let kind = 'unit';
  if (card?.isSite) kind = 'site';
  else if (card?.type === 'Avatar') kind = 'avatar';
  const ownerless = kind === 'site' && OWNERLESS_SITE_IDS.has(card?.cardId);
  return {
    kind,
    instanceId: card?.id,
    seat: ownerless ? null : seatFromOrientation(card),
    ownerless,
  };
}

/**
 * Cells the attacker can attack from its current cell: the unit's OWN
 * location, nothing else. Codex Attack. "You may only attack enemy units
 * or the surface of enemy sites that are at your location." Adjacency is
 * reached by the Move-and-Attack movement step (step onto the enemy's
 * square, then strike). Ranged is NOT part of the attack envelope. It's
 * a separate activated ability ("Tap → Shoot a projectile"), modelled by
 * getProjectileHits in ranged.js.
 *
 * `attackerCard` is accepted for signature stability but unused.
 *
 * @returns Array of { col, row }
 */
export function getAttackableCells(fromCol, fromRow, totalCols, totalRows, attackerCard = null) {
  void attackerCard;
  if (fromCol < 0 || fromRow < 0 || fromCol >= totalCols || fromRow >= totalRows) return [];
  return [{ col: fromCol, row: fromRow }];
}

/**
 * Valid attack targets at (targetCol, targetRow) from the attacker's
 * perspective. Returns a list mixing cards and the site itself.
 *
 * Surface-only: subsurface cards (card.subsurface set) are excluded.
 * Self-exclusion: the attacker is never a valid target.
 * Controller filter: only non-controlled cards are targetable; the
 *   site is targetable if controlled by an opponent (or any opponent
 *   in 2P).
 *
 * @param attackerCard       cardInstance doing the attacking
 * @param targetCol/targetRow target cell coordinates
 * @param meshes             live Three.js mesh map
 * @param cellAt             (x, z) → { col, row } locator
 * @returns Array of { kind: 'card' | 'site', card }
 */
export function getValidAttackTargets(attackerCard, targetCol, targetRow, meshes, cellAt, opts = {}) {
  const attackerControllerRotated = !!attackerCard?.rotated;
  const attackerIsAirborne = hasStatus(attackerCard, 'airborne');
  // Region the attacker currently occupies: null == surface, otherwise
  // 'underground' / 'underwater'. A subsurface attacker may only strike
  // same-region subsurface units and can NOT attack sites or surface
  // units (Bugs 4 + 10). Mirrors the region-keyed model the ranged path
  // already uses (hittableUnitsAt keys on shooter.subsurface).
  const attackerRegion = attackerCard?.subsurface || null;
  // v1.76. When set, the caller has determined the attacker can
  // perform a region transition (burrow / submerge) at THIS cell using
  // remaining step budget. canTransitionTo is a Set of 'underground'
  // and/or 'underwater'. Same-cell subsurface targets matching one of
  // those regions become attackable, tagged with requiresRegionTransition
  // so the apply path auto-transitions the attacker before damage.
  const canTransitionTo = opts.canTransitionTo || null;
  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (card.id === attackerCard?.id) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || cell.col !== targetCol || cell.row !== targetRow) continue;
    // Friendly cards aren't targetable. Non-rotated == p1 controlled,
    // rotated == p2 controlled. Same convention as the rest of the
    // engine.
    if (!!card.rotated === attackerControllerRotated) continue;
    // Only sites and units are attackable (codex Attack: "enemy units or
    // the surface of enemy sites"). A unit is a minion (incl. Automatons,
    // which are artifact-minions) or an avatar. Relics, carriable
    // artifacts and monuments are never attack targets. Mirrors the
    // unit gate the ranged paths already apply (hittableUnitsAt).
    if (!card.isSite && !countsAsUnit(card)) continue;
    // Subsurface attacker (Bugs 4 + 10): the attacker is below ground /
    // water and may only strike same-region subsurface units. Sites are
    // surface structures and surface units are out of reach, so both are
    // excluded; the burrow/submerge transition machinery does NOT apply
    // (the attacker is already in-region and strikes peers directly).
    if (attackerRegion) {
      if (card.isSite) continue;
      if ((card.subsurface || null) !== attackerRegion) continue;
      // same-region buried/submerged peer. Falls through to the airborne/
      // stealth gates and is emitted as a normal card target below.
    } else if (card.subsurface) {
      // Surface attacker vs a buried target: normally excluded (surface
      // attack can't reach below ground). v1.76. If the attacker has the
      // matching movement keyword AND has step budget remaining for the
      // burrow/submerge transition (caller computes both into
      // canTransitionTo), the target becomes attackable with a
      // requiresRegionTransition tag. The apply path consumes the tag,
      // sets attacker.subsurface, and resolves the strike from below-ground.
      if (!canTransitionTo) continue;
      if (card.subsurface === 'underground' && canTransitionTo.has('underground')) {
        out.push({ kind: 'card', card, requiresRegionTransition: 'underground' });
      } else if (card.subsurface === 'underwater' && canTransitionTo.has('underwater')) {
        out.push({ kind: 'card', card, requiresRegionTransition: 'underwater' });
      }
      continue;
    }
    // Airborne defenders can only be targeted by Airborne attackers
    // (codex Airborne entry). Sites are never Airborne so the gate
    // doesn't apply to them.
    if (!card.isSite && hasStatus(card, 'airborne') && !attackerIsAirborne) continue;
    // Stealth defenders can't be targeted by opponents' attacks
    // (codex Stealth entry). Sites can't have Stealth so they pass
    // through. hasActiveStealth already gates on isDisabled.
    if (!card.isSite && hasActiveStealth(card)) continue;
    if (card.isSite) {
      out.push({ kind: 'site', card });
    } else {
      out.push({ kind: 'card', card });
    }
  }
  return out;
}

const hasStatus = hasKeyword;

/**
 * Codex Lethal: "Any damage a Lethal unit deals to a minion is
 * enough to kill it." Returns true when the engine should set the
 * defender's currentLife to 0 regardless of remaining HP.
 *
 * Exemptions:
 *   • Damage of 0 (Ward absorbed, immunity, etc.). "zero damage
 *     isn't damage at all" per codex.
 *   • Avatars. They only die via death blow at Death's Door, not
 *     via "killed" effects.
 *   • Sites. No HP; Lethal does nothing useful against them.
 *
 * Lethal status is checked the same as every other keyword: printed,
 * aura-granted, or manually applied.
 */
export function attackerHasLethalKill(attacker, defender, damageDealt) {
  if (!attacker || !defender) return false;
  if (!Number.isFinite(damageDealt) || damageDealt <= 0) return false;
  if (defender.isSite) return false;
  if (defender.type === 'Avatar') return false;
  return hasStatus(attacker, 'lethal');
}

/**
 * Damage-exchange resolution between attacker and defender.
 * Default Sorcery strike is simultaneous: both deal their power to
 * the other. Wards are inspected at the caller (engine/automation/
 * ops/wards.js). This pure module returns raw damage numbers and
 * the caller applies Ward interception + subtracts from currentLife.
 *
 * @param attacker cardInstance with .currentAttack (effective power)
 * @param defender cardInstance with .currentAttack (effective power)
 * Or for site targets, a card with .currentAttack=0
 *                 (sites don't strike back).
 * @returns {{
 *   damageToDefender: number,
 *   damageToAttacker: number,
 *   sourceCardId: string,
 *   defenderCardId: string,
 * }}
 */
export function resolveAttack(attacker, defender, opts = {}) {
  const attackerPower = Math.max(0, Number(attacker?.currentAttack ?? attacker?.baseAttack ?? 0));
  const defenderPower = Math.max(0, Number(defender?.currentAttack ?? defender?.baseAttack ?? 0));
  // Sites don't strike back. Defender power applies only when the
  // defender is a unit (not a site).
  let defenderStrikes = !defender?.isSite;
  // v1.56. Ranged projectile attack: when attacker fired from outside
  // melee range, the defender deals no damage back. Caller passes cell
  // coordinates; if they're > 1 Manhattan apart and the attacker has
  // Ranged, suppress the counter-strike. Adjacent Ranged attacks (e.g.
  // a Ranged archer poking the minion next door) still trade blows
  // normally. The codex projectile rule only kicks in at distance.
  if (defenderStrikes && opts.attackerCell && opts.defenderCell && getRangedValue(attacker) > 0) {
    if (isProjectileAttack(opts.attackerCell, opts.defenderCell)) defenderStrikes = false;
  }
  return {
    damageToDefender: attackerPower,
    damageToAttacker: defenderStrikes ? defenderPower : 0,
    sourceCardId: attacker?.id,
    defenderCardId: defender?.id,
    projectile: defenderStrikes ? false : (getRangedValue(attacker) > 0 && !!opts.attackerCell && !!opts.defenderCell && isProjectileAttack(opts.attackerCell, opts.defenderCell)),
  };
}
